import { useTranslation } from '@rhino-project/core/i18n';
import React from 'react';
import { Button, Card } from '@heroui/react';
import { Icon } from '@iconify/react';
import type {
  RhinoFilter,
  RhinoOrder,
  RhinoOrderTerm,
  RhinoRead,
  RhinoResourceName
} from '@rhino-project/core';
import { useModelIndexController } from '@rhino-project/core/hooks';

type PayloadIdentity = { id: number; display_name: string | null };

const PLAYER_INDEX_PAGE_SIZE = 5;

// Every section here lists records hanging off one player, newest first, so the
// model has to be filterable by that association and orderable by recency for
// the defaults below to mean anything. Naming the requirements makes a model
// that lacks either a compile error rather than a clause the sieve quietly
// drops.
type PlayerScopedModel = {
  [K in RhinoResourceName]: 'player_id' extends keyof RhinoFilter<K>
    ? '-created_at' extends RhinoOrderTerm<K>
      ? K
      : never
    : never;
}[RhinoResourceName];

// Generic over the model it lists so `render` receives that model's payload
// rather than an untyped bag.
interface PlayerIndexSectionProps<M extends PlayerScopedModel> {
  baseModel: M;
  player: Pick<RhinoRead<'player'>, 'id'>;
  render?: (resource: RhinoRead<M>) => React.ReactNode;
  title: React.ReactNode;
  emptyMessage?: string;
  before?: React.ReactNode;
  filter?: RhinoFilter<M>;
  order?: RhinoOrder<M> | null;
}

const PlayerIndexSection = <M extends PlayerScopedModel>({
  baseModel,
  player,
  render,
  title,
  emptyMessage,
  before,
  filter,
  order
}: PlayerIndexSectionProps<M>) => {
  const { t } = useTranslation();

  // PlayerScopedModel guarantees the key exists; the constraint does not
  // survive into the deferred conditional M resolves through.
  const resolvedFilter = filter ?? ({ player_id: player.id } as RhinoFilter<M>);
  // Omitting the prop takes the newest first; null asks for no ordering at all.
  // PlayerScopedModel guarantees the term, on the same footing as the filter key
  // above and needing the same assertion to survive M's deferred conditional.
  const resolvedOrder =
    order === undefined ? (['-created_at'] as RhinoOrder<M>) : order;

  const { results, page, totalPages, hasPrevPage, hasNextPage, setPage } =
    useModelIndexController({
      model: baseModel,
      analyticsSurface: 'player_detail',
      defaultFilter: resolvedFilter,
      defaultFiltersBaseOwner: false,
      defaultOrder: resolvedOrder,
      defaultLimit: PLAYER_INDEX_PAGE_SIZE
    });

  // The controller's own result type fans out over every model while M is
  // still a parameter and does not collapse once it is known, so the
  // correlation is asserted once here rather than at each consumer.
  const items = results as RhinoRead<M>[] | null;

  return (
    <Card className="my-2">
      <Card.Header className="flex-row justify-between items-center">
        <Card.Title>{title}</Card.Title>
        {totalPages > 1 && (
          <div className="flex items-center gap-1 text-sm">
            <Button
              isIconOnly
              variant="ghost"
              size="sm"
              aria-label={t('frontend.messages.previous_page')}
              isDisabled={!hasPrevPage}
              onPress={() => setPage(page - 1)}
            >
              <Icon icon="bi:chevron-left" />
            </Button>
            <span className="text-muted">
              {page}/{totalPages}
            </span>
            <Button
              isIconOnly
              variant="ghost"
              size="sm"
              aria-label={t('frontend.messages.next_page')}
              isDisabled={!hasNextPage}
              onPress={() => setPage(page + 1)}
            >
              <Icon icon="bi:chevron-right" />
            </Button>
          </div>
        )}
      </Card.Header>
      <Card.Content>
        {before}
        {items?.length === 0 && (
          <div className="text-muted text-sm py-2">
            {emptyMessage ?? t('frontend.messages.none_yet')}
          </div>
        )}
        {items?.map((item) =>
          render ? (
            render(item)
          ) : (
            // The default rendering reads the two fields every payload
            // carries, which TypeScript cannot see while M is a parameter.
            <div key={(item as PayloadIdentity).id}>
              {(item as PayloadIdentity).display_name}
            </div>
          )
        )}
      </Card.Content>
    </Card>
  );
};

export default PlayerIndexSection;
